import { ProductImageComponent } from '../shared/product-image/product-image.component'
import { Component, OnInit, inject } from '@angular/core'
import { Router, RouterLink } from '@angular/router'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import {
  MarketplaceService,
  RequisitionCartItem,
} from '../../services/marketplace.service'
import { TranslationService } from '../../services/translation.service'
import { RouteService } from '../../services/route.service'
import { ROUTES } from '../../constants/routes'
import { RequisitionService } from '../../services/requisition.service'

@Component({
  selector: 'app-requisition-cart',
  imports: [
    ProductImageComponent,
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './requisition-cart.component.html',
  styleUrl: './requisition-cart.component.scss',
})
export class RequisitionCartComponent implements OnInit {
  cart = inject(MarketplaceService)
  t = inject(TranslationService)
  private routeService = inject(RouteService)
  private requisitions = inject(RequisitionService)
  private router = inject(Router)
  creating = false
  createError = ''
  quantityDrafts: Record<string, string> = {}
  invalidQuantityId: string | null = null

  ngOnInit(): void {
    this.cart.loadCart(true)
    this.routeService.setRoute(
      this.routeService.getUserRoutes(
        ROUTES.REQUISITION_CART.title,
        ROUTES.REQUISITION_CART.path,
      ),
    )
  }

  price(item: RequisitionCartItem): string {
    const product = item.product
    if (!product || product.priceAmount === null)
      return this.t.getMarketplacePriceNotProvidedText()
    if (!product.priceCurrency)
      return new Intl.NumberFormat().format(product.priceAmount)
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: product.priceCurrency,
      currencyDisplay: 'code',
    }).format(product.priceAmount)
  }

  quantityValue(item: RequisitionCartItem): string {
    return this.quantityDrafts[item.productId] ?? String(item.quantity)
  }

  wholeUnit(item: RequisitionCartItem): boolean {
    return this.cart.wholeUnit(item.product?.requestUnit)
  }

  quantityChanged(id: string, event: Event): void {
    this.quantityDrafts[id] = (event.target as HTMLInputElement).value
    if (this.invalidQuantityId === id) this.invalidQuantityId = null
  }

  saveQuantity(item: RequisitionCartItem): void {
    const raw = this.quantityDrafts[item.productId]
    if (raw === undefined) return
    if (this.cart.pendingId() === item.productId) return
    const quantity = Number(raw)
    if (
      !raw.trim() ||
      !this.cart.validQuantity(quantity, item.product?.requestUnit)
    ) {
      this.invalidQuantityId = item.productId
      return
    }
    if (quantity === item.quantity) {
      delete this.quantityDrafts[item.productId]
      return
    }
    this.cart.updateQuantity(item.productId, quantity).subscribe((saved) => {
      if (saved) delete this.quantityDrafts[item.productId]
    })
  }

  removeItem(id: string): void {
    delete this.quantityDrafts[id]
    if (this.invalidQuantityId === id) this.invalidQuantityId = null
    this.cart.removeSelection(id)
  }

  createDraft(): void {
    if (
      this.creating ||
      this.cart.cartLoading() ||
      this.cart.cartLoadFailed() ||
      !this.cart.cartCount()
    )
      return
    if (this.cart.cartItems().some((item) => item.reason)) {
      this.createError = this.t.getRequisitionIneligibleText()
      return
    }
    const id = this.cart.cartGenerationId()
    const revision = this.cart.cartRevision()
    if (!id || revision === null) return
    this.creating = true
    this.createError = ''
    this.requisitions.createFromCart(id, revision).subscribe({
      next: (request) => {
        this.creating = false
        this.cart.loadCart(true)
        this.router.navigate(['/requisitions', request.id])
      },
      error: (error) => {
        // A lost response may still mean the conversion committed.
        this.requisitions.get(id).subscribe({
          next: (request) => {
            this.creating = false
            this.cart.loadCart(true)
            this.router.navigate(['/requisitions', request.id])
          },
          error: () => {
            this.creating = false
            this.createError = this.t.getRequisitionErrorText(
              error.error?.reason,
            )
            if (error.error?.reason === 'cart_changed') this.cart.loadCart(true)
          },
        })
      },
    })
  }
}
